"use strict";
/*---------------------------------------------------------------------------------------------
*  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
*--------------------------------------------------------------------------------------------*/
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DotnetPathFinder = void 0;
const CommandExecutor_1 = require("../Utils/CommandExecutor");
const lodash = require("lodash");
const os = require("os");
const path = require("path");
const EventStreamEvents_1 = require("../EventStream/EventStreamEvents");
const FileUtilities_1 = require("../Utils/FileUtilities");
const TypescriptUtilities_1 = require("../Utils/TypescriptUtilities");
const CacheTimeConstants_1 = require("./CacheTimeConstants");
const DotnetConditionValidator_1 = require("./DotnetConditionValidator");
const RegistryReader_1 = require("./RegistryReader");
class DotnetPathFinder {
    constructor(workerContext, utilityContext, executor, file) {
        var _a, _b;
        this.workerContext = workerContext;
        this.utilityContext = utilityContext;
        this.executor = executor;
        this.file = file;
        (_a = this.executor) !== null && _a !== void 0 ? _a : (this.executor = new CommandExecutor_1.CommandExecutor(this.workerContext, this.utilityContext));
        (_b = this.file) !== null && _b !== void 0 ? _b : (this.file = new FileUtilities_1.FileUtilities());
    }
    /**
     *
     * @returns The DOTNET_ROOT environment variable, which is the root path for the dotnet installation.
     * Some applications, such as `dotnet test`, prefer DOTNET_ROOT over the PATH setting.
     * DOTNET_ROOT is also not the only setting.
     * DOTNET_ROOT(x86) - Deprecated. Only used when running 32-bit executables. VS Code 32 bit is deprecated, so don't support this.
     * DOTNET_ROOT_X86 - The non deprecated version of the above variable, still ignore.
     * DOTNET_ROOT_X64 - Used when running 64-bit executables on an ARM64 OS.
     * Node only runs on x64 and not ARM, but that doesn't mean the .NET Application won't run on ARM.
     *
     * DOTNET_HOST_PATH may be set but this is owned by the host. Don't respect any user setting here.
     *
     * The VS Code Workspace environment may also be different from the System environment.
     */
    findDotnetRootPath(requestedArchitecture) {
        return __awaiter(this, void 0, void 0, function* () {
            this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathLookupRootPATH(`Looking up .NET on the root.`));
            if (requestedArchitecture === 'x64' && (this.executor !== undefined ? (yield (0, TypescriptUtilities_1.getOSArch)(this.executor)).includes('arm') : false)) {
                let dotnetOnRootEmulationPath = process.env.DOTNET_ROOT_X64;
                if ((0, TypescriptUtilities_1.EnvironmentVariableIsDefined)(dotnetOnRootEmulationPath)) {
                    // DOTNET_ROOT should be set to the directory containing the dotnet executable, not the executable itself.
                    // https://learn.microsoft.com/en-us/dotnet/core/tools/dotnet-environment-variables
                    dotnetOnRootEmulationPath = path.join(dotnetOnRootEmulationPath, (0, TypescriptUtilities_1.getDotnetExecutable)());
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathRootEmulationPATHFound(`Under emulation and emulation root is set to ${dotnetOnRootEmulationPath}.`));
                    return dotnetOnRootEmulationPath;
                }
                else {
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathRootUnderEmulationButNoneSet(`Under emulation but DOTNET_ROOT_X64 is not set.`));
                }
            }
            let dotnetOnRootPath = process.env.DOTNET_ROOT;
            if ((0, TypescriptUtilities_1.EnvironmentVariableIsDefined)(dotnetOnRootPath)) {
                // DOTNET_ROOT should be set to the directory containing the dotnet executable, not the executable itself.
                dotnetOnRootPath = path.join(dotnetOnRootPath, (0, TypescriptUtilities_1.getDotnetExecutable)());
                this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathRootPATHFound(`Found .NET on the root: ${dotnetOnRootPath}`));
                return dotnetOnRootPath;
            }
            return undefined;
        });
    }
    /**
     * @remarks This only checks dotnet --list-runtimes or --list-sdks, so it can be more performant for the base case.
     * This allows skipping which, where, and also does not rely on --info which is slower because it shells to the SDK instead of just being the host.
     * @returns The path to the dotnet executable, which may be a symlink to the actual executable.
     */
    findDotnetFastFromListOnly() {
        return __awaiter(this, void 0, void 0, function* () {
            const oldLookup = process.env.DOTNET_MULTILEVEL_LOOKUP;
            try {
                process.env.DOTNET_MULTILEVEL_LOOKUP = '0'; // make it so --list-runtimes only finds the runtimes on that path: https://learn.microsoft.com/en-us/dotnet/core/compatibility/deployment/7.0/multilevel-lookup#reason-for-change
                const finalPath = yield this.getTruePath(['dotnet']);
                return this.returnWithRestoringEnvironment(finalPath, 'DOTNET_MULTILEVEL_LOOKUP', oldLookup);
            }
            finally {
                process.env.DOTNET_MULTILEVEL_LOOKUP = oldLookup; // Ensure the environment variable is always restored
            }
        });
    }
    /**
     *
     * @returns A set of the path environment variable(s) for which or where dotnet, which may need to be converted to the actual path if it points to a polymorphic executable.
     * For example, `snap` installs dotnet to snap/bin/dotnet, which you can call --list-runtimes on.
     * The 'realpath' of that is 'usr/bin/snap', which you cannot invoke --list-runtimes on, because it is snap.
     * In this case, we need to use this polymorphic path to find the actual path later.
     *
     * In an install such as homebrew, the PATH is not indicative of all of the PATHs. So dotnet may be missing in the PATH even though it is found in an alternative shell.
     * The PATH can be discovered using path_helper on mac.
     */
    findRawPathEnvironmentSetting() {
        return __awaiter(this, arguments, void 0, function* (tryUseTrueShell = true) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
            const oldLookup = process.env.DOTNET_MULTILEVEL_LOOKUP;
            process.env.DOTNET_MULTILEVEL_LOOKUP = '0'; // make it so --list-runtimes only finds the runtimes on that path: https://learn.microsoft.com/en-us/dotnet/core/compatibility/deployment/7.0/multilevel-lookup#reason-for-change
            const searchEnvironment = process.env; // this is the default, but sometimes it does not get picked up
            const options = tryUseTrueShell && os.platform() !== 'win32' ?
                {
                    // Use /bin/bash as the default shell, but if it's not available try to use /bin/sh
                    // Users may see different behavior due to this if they have a custom shell such as zsh but those shells may have unexpected behaviors.
                    env: searchEnvironment, shell: process.env.SHELL === '/bin/bash' ? '/bin/bash' : '/bin/sh', dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.SYS_CMD_SEARCH_CACHE_DURATION_MS
                }
                :
                    {
                        env: searchEnvironment, dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.SYS_CMD_SEARCH_CACHE_DURATION_MS
                    };
            // Log the PATH for diagnostic debugging via logs.
            this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathLookupPATH(`Looking up .NET on the path. Process.env.path: ${process.env.PATH}`));
            if (os.platform() === 'win32') {
                (_a = this.executor) === null || _a === void 0 ? void 0 : _a.execute(CommandExecutor_1.CommandExecutor.makeCommand('echo', ['%PATH%']), { dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.DOTNET_INFORMATION_CACHE_DURATION_MS }, false).then((result) => {
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathLookupPATH(`Execution Path (Win): ${result === null || result === void 0 ? void 0 : result.stdout}`));
                }).catch(() => { });
            }
            else {
                (_b = this.executor) === null || _b === void 0 ? void 0 : _b.execute(CommandExecutor_1.CommandExecutor.makeCommand('echo', ['$PATH']), { dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.DOTNET_INFORMATION_CACHE_DURATION_MS }, false).then((result) => {
                    // Log the default shell state
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathLookupPATH(`Execution Path (Unix-Default-Shell): ${result === null || result === void 0 ? void 0 : result.stdout}`));
                }).catch(() => { });
                if (!(options.shell === '/bin/bash')) {
                    (_c = this.executor) === null || _c === void 0 ? void 0 : _c.execute(CommandExecutor_1.CommandExecutor.makeCommand('echo', ['$PATH']), { shell: 'bin/bash', dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.SYS_CMD_SEARCH_CACHE_DURATION_MS }, false).then((result) => {
                        this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathLookupPATH(`Execution Path (Unix Bash): ${result === null || result === void 0 ? void 0 : result.stdout}`));
                    }).catch(() => { });
                }
            }
            let pathLocatorCommand = '';
            if (os.platform() === 'win32') {
                pathLocatorCommand = (_f = (_e = (yield ((_d = this.executor) === null || _d === void 0 ? void 0 : _d.tryFindWorkingCommand([
                    // We have to give the command an argument to return status 0, and the only thing its guaranteed to find is itself :)
                    CommandExecutor_1.CommandExecutor.makeCommand('where', ['where']),
                    CommandExecutor_1.CommandExecutor.makeCommand('where.exe', ['where.exe']),
                    CommandExecutor_1.CommandExecutor.makeCommand('%SystemRoot%\\System32\\where.exe', ['%SystemRoot%\\System32\\where.exe']), // if PATH is corrupted
                    CommandExecutor_1.CommandExecutor.makeCommand('C:\\Windows\\System32\\where.exe', ['C:\\Windows\\System32\\where.exe']) // in case SystemRoot is corrupted, best effort guess
                ], options)))) === null || _e === void 0 ? void 0 : _e.commandRoot) !== null && _f !== void 0 ? _f : 'where';
            }
            else {
                pathLocatorCommand = (_j = (_h = (yield ((_g = this.executor) === null || _g === void 0 ? void 0 : _g.tryFindWorkingCommand([
                    CommandExecutor_1.CommandExecutor.makeCommand('which', ['which']),
                    CommandExecutor_1.CommandExecutor.makeCommand('/usr/bin/which', ['/usr/bin/which']), // if PATH is corrupted
                ], options)))) === null || _h === void 0 ? void 0 : _h.commandRoot) !== null && _j !== void 0 ? _j : 'which';
            }
            options.dotnetInstallToolCacheTtlMs = CacheTimeConstants_1.DOTNET_INFORMATION_CACHE_DURATION_MS;
            const findCommand = CommandExecutor_1.CommandExecutor.makeCommand(pathLocatorCommand, ['dotnet']);
            const dotnetsOnPATH = (_l = (yield ((_k = this.executor) === null || _k === void 0 ? void 0 : _k.execute(findCommand, options, false)))) === null || _l === void 0 ? void 0 : _l.stdout.split('\n').map(x => x.trim()).filter(x => x !== '');
            if (dotnetsOnPATH && ((_m = dotnetsOnPATH === null || dotnetsOnPATH === void 0 ? void 0 : dotnetsOnPATH.length) !== null && _m !== void 0 ? _m : 0) > 0) {
                this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathPATHFound(`Found.NET on the path: ${JSON.stringify(dotnetsOnPATH)}`));
                return this.returnWithRestoringEnvironment(yield this.getTruePath(dotnetsOnPATH), 'DOTNET_MULTILEVEL_LOOKUP', oldLookup);
            }
            else {
                const pathsOnPATH = (_o = process.env.PATH) === null || _o === void 0 ? void 0 : _o.split((0, TypescriptUtilities_1.getPathSeparator)());
                const validPathsOnPATH = [];
                if (pathsOnPATH && ((_p = pathsOnPATH === null || pathsOnPATH === void 0 ? void 0 : pathsOnPATH.length) !== null && _p !== void 0 ? _p : 0) > 0) {
                    const dotnetExecutable = (0, TypescriptUtilities_1.getDotnetExecutable)();
                    for (const pathOnPATH of pathsOnPATH) {
                        const resolvedDotnetPath = path.join(pathOnPATH, dotnetExecutable);
                        if (yield this.file.exists(resolvedDotnetPath)) {
                            this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathLookupPATH(`Looking up.NET on the path by processing PATH string.resolved: ${resolvedDotnetPath}.`));
                            validPathsOnPATH.push(resolvedDotnetPath);
                        }
                    }
                }
                if (((_q = validPathsOnPATH === null || validPathsOnPATH === void 0 ? void 0 : validPathsOnPATH.length) !== null && _q !== void 0 ? _q : 0) > 0) {
                    return this.returnWithRestoringEnvironment(validPathsOnPATH, 'DOTNET_MULTILEVEL_LOOKUP', oldLookup);
                }
            }
            return this.returnWithRestoringEnvironment(undefined, 'DOTNET_MULTILEVEL_LOOKUP', oldLookup);
        });
    }
    // eslint-disable-next-line @typescript-eslint/require-await
    returnWithRestoringEnvironment(returnValue, envVarToRestore, envResToRestore) {
        return __awaiter(this, void 0, void 0, function* () {
            if ((0, TypescriptUtilities_1.EnvironmentVariableIsDefined)(envVarToRestore)) {
                process.env[envVarToRestore] = envResToRestore;
            }
            else {
                delete process.env[envVarToRestore];
            }
            return returnValue;
        });
    }
    /**
     * @returns The 'realpath' or resolved path for dotnet from which or where dotnet.
     * Some installers, such as the Ubuntu install with the PMC Feed, the PATH is set to /usr/bin/dotnet which is a symlink to /usr/share/dotnet.
     * If we want to return the actual path, we need to use realpath.
     *
     * We can't use realpath on all paths, because some paths are polymorphic executables and the realpath is invalid.
     */
    findRealPathEnvironmentSetting() {
        return __awaiter(this, arguments, void 0, function* (tryUseTrueShell = true) {
            var _a, _b;
            this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathLookupRealPATH(`Looking up.NET on the real path.`));
            const dotnetsOnPATH = yield this.findRawPathEnvironmentSetting(tryUseTrueShell);
            const realPaths = [];
            for (const dotnetOnPATH of dotnetsOnPATH !== null && dotnetsOnPATH !== void 0 ? dotnetsOnPATH : []) {
                const resolvedPath = yield ((_a = this.file) === null || _a === void 0 ? void 0 : _a.realpath(dotnetOnPATH));
                if (resolvedPath) {
                    realPaths.push(resolvedPath);
                }
            }
            this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathRealPATHFound(`Found.NET on the path: ${JSON.stringify(dotnetsOnPATH)}, realpath: ${realPaths}`));
            if (((_b = realPaths === null || realPaths === void 0 ? void 0 : realPaths.length) !== null && _b !== void 0 ? _b : 0) > 0) {
                return undefined;
            }
            return this.getTruePath(realPaths);
        });
    }
    findHostInstallPaths(requestedArchitecture) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathHostFxrResolutionLookup(`Looking up .NET without checking the PATH.`));
            const oldLookup = process.env.DOTNET_MULTILEVEL_LOOKUP;
            process.env.DOTNET_MULTILEVEL_LOOKUP = '0';
            if (os.platform() === 'win32') {
                const registryReader = new RegistryReader_1.RegistryReader(this.workerContext, this.utilityContext, this.executor);
                const hostPathWin = yield registryReader.getHostLocation(requestedArchitecture);
                const realHostPathWin = hostPathWin ? yield ((_a = this.file) === null || _a === void 0 ? void 0 : _a.realpath(hostPathWin)) : null;
                const paths = [];
                if (hostPathWin) {
                    paths.push(path.join(hostPathWin, (0, TypescriptUtilities_1.getDotnetExecutable)()));
                }
                if (realHostPathWin) {
                    paths.push(path.join(realHostPathWin, (0, TypescriptUtilities_1.getDotnetExecutable)()));
                }
                if (paths.length > 0) {
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathOnRegistry(`The host could be found in the registry.${JSON.stringify(paths)}`));
                }
                else {
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathNoHostOnRegistry(`The host could not be found in the registry`));
                }
                return this.returnWithRestoringEnvironment(yield this.getTruePath(lodash.uniq(paths)), 'DOTNET_MULTILEVEL_LOOKUP', oldLookup);
            }
            else {
                // Possible values for arch are: x86, x64, arm32, arm64
                // x86 and arm32 are not a concern since 32 bit vscode is out of support and not needed by other extensions
                // https://github.com/dotnet/designs/blob/main/accepted/2021/install-location-per-architecture.md#new-format
                const paths = [];
                const netSixAndAboveHostInstallSaveLocation = `/etc/dotnet/install_location_${requestedArchitecture}`;
                const netFiveAndNetSixAboveFallBackInstallSaveLocation = `/etc/dotnet/install_location`;
                paths.push(...(yield this.getPathsFromEtc(netSixAndAboveHostInstallSaveLocation)));
                paths.push(...(yield this.getPathsFromEtc(netFiveAndNetSixAboveFallBackInstallSaveLocation)));
                if (paths.length > 0) {
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathOnFileSystem(`The host could be found in the file system.${JSON.stringify(paths)}`));
                }
                else {
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathNoHostOnFileSystem(`The host could not be found in the file system.`));
                }
                return this.returnWithRestoringEnvironment(yield this.getTruePath(lodash.uniq(paths)), 'DOTNET_MULTILEVEL_LOOKUP', oldLookup);
            }
        });
    }
    getPathsFromEtc(etcLoc) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const paths = [];
            if (yield this.file.exists(etcLoc)) {
                try {
                    const installPath = (yield this.file.read(etcLoc)).trim();
                    paths.push(path.join(installPath, (0, TypescriptUtilities_1.getDotnetExecutable)()));
                    const realPath = yield ((_a = this.file) === null || _a === void 0 ? void 0 : _a.realpath(installPath));
                    if (realPath) {
                        paths.push(path.join(realPath, (0, TypescriptUtilities_1.getDotnetExecutable)()));
                    }
                }
                catch (error) // eslint-disable-line @typescript-eslint/no-explicit-any
                 {
                    // readfile throws if the file gets deleted in between the existing check and now
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    this.workerContext.eventStream.post(new EventStreamEvents_1.FileDoesNotExist(`The host was deleted after checking in the file system.${error === null || error === void 0 ? void 0 : error.message} ${etcLoc}`));
                }
            }
            else {
                this.workerContext.eventStream.post(new EventStreamEvents_1.FileDoesNotExist(`The host save location never existed at ${etcLoc}`));
            }
            return paths;
        });
    }
    /**
     *
     * @param tentativePaths Paths that may hold a dotnet executable.
     * @returns The actual physical location/path on disk where the executables lie for each of the paths.
     * Some of the symlinks etc resolve to a path which works but is still not the actual path.
     */
    getTruePath(tentativePaths) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const truePaths = [];
            for (const tentativePath of tentativePaths) {
                // This will even work if only the sdk is installed, list-runtimes on an sdk installed host would work
                const runtimeInfo = yield new DotnetConditionValidator_1.DotnetConditionValidator(this.workerContext, this.utilityContext, this.executor).getRuntimes(tentativePath);
                if (((_a = runtimeInfo === null || runtimeInfo === void 0 ? void 0 : runtimeInfo.length) !== null && _a !== void 0 ? _a : 0) > 0) {
                    // q.t. from @dibarbet on the C# Extension:
                    // The .NET install layout is a well known structure on all platforms.
                    // See https://github.com/dotnet/designs/blob/main/accepted/2020/install-locations.md#net-core-install-layout
                    //
                    // Therefore we know that the runtime path is always in <install root>/shared/<runtime name>
                    // and the dotnet executable is always at <install root>/dotnet(.exe).
                    //
                    // Since dotnet --list-runtimes will always use the real assembly path to output the runtime folder (no symlinks!)
                    // we know the dotnet executable will be two folders up in the install root.
                    truePaths.push(path.join(path.dirname(path.dirname(runtimeInfo[0].directory)), (0, TypescriptUtilities_1.getDotnetExecutable)()));
                }
                else {
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathNoRuntimesOnHost(`The host: ${tentativePath} does not contain a .NET runtime installation.`));
                }
            }
            return ((_b = truePaths === null || truePaths === void 0 ? void 0 : truePaths.length) !== null && _b !== void 0 ? _b : 0) > 0 ? truePaths : tentativePaths;
        });
    }
}
exports.DotnetPathFinder = DotnetPathFinder;
//# sourceMappingURL=DotnetPathFinder.js.map